import { css } from 'aphrodite';
import { ISettingEntry, toDetailRow } from './settingEntry';
import { settingsHubStyles as styles } from './settingsHubStyles';

interface IProps {
	parent: ISettingEntry;
	/** The dependents to show beneath the parent. Empty renders the parent on its own. */
	dependents: ISettingEntry[];
	/** Sits beside the parent's label — a dependent count, or a note that it is only context. */
	parentBadge?: React.ReactNode;
	/**
	 * Drops the chunk's trailing line, for the last chunk in a list — the panel's own edge already
	 * closes it off. A chunk draws that line in one of two places depending on whether it has
	 * dependents, so the caller cannot suppress it from outside.
	 */
	hideDivider?: boolean;
}

/**
 * A parent setting and its dependents as one visual unit: the parent, then an inset panel whose
 * caption names the relationship, with the separating line drawn below the whole chunk rather than
 * between the parent and the dependents it owns.
 *
 * Shared by the group view and search results so the two cannot drift.
 */
export function SettingDependencyChunk({ parent, dependents, parentBadge, hideDivider }: IProps) {
	if (dependents.length === 0) {
		return toDetailRow(parent, { badge: parentBadge, hideDivider });
	}

	// Only toggle-shaped parents report On/Off; anything else is treated as off, since its dependents
	// are locked either way.
	const isParentOn = parent.valueText === 'On';
	const parentName = parent.labelText;

	// Heads both states: naming the parent is what tells you these rows are not free-standing, and an
	// inset alone does not say which setting above owns them.
	const caption = isParentOn
		? parentName
			? `Applies while ${parentName} is on`
			: 'Applies while the setting above is on'
		: parentName
			? `Turn on ${parentName} to edit these`
			: 'Turn on the setting above to edit these';

	return (
		<div className={css(styles.dependencyGroup, hideDivider && styles.dependencyGroupLast)}>
			{toDetailRow(parent, { badge: parentBadge, hideDivider: true })}
			<div className={css(styles.dependentPanel, isParentOn && styles.dependentPanelActive)}>
				<div className={css(styles.dependentCaption)}>{caption}</div>
				<div className={css(styles.dependentRows)}>
					{dependents.map((dependent, index) =>
						toDetailRow(dependent, { hideDivider: index === dependents.length - 1 })
					)}
				</div>
			</div>
		</div>
	);
}
